/**
 * Settings page.
 */
import Account from '@shared/assets/images/png/settings/account.png';
import Channels from '@shared/assets/images/png/settings/channels.png';
import Disk from '@shared/assets/images/png/settings/disk.png';
import Features from '@shared/types/enums/features';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import Language from '@shared/assets/images/png/settings/language.png';
import Logout from '@shared/assets/images/png/settings/logout.png';
import Modals from '@shared/types/enums/modals';
import MosaicTemplate from '@components/templates/mosaic';
import Pages from '@pages';
import PlanUtils from '@shared/utils/plan';
import PowerSaving from '@shared/assets/images/png/settings/powerSaving.png';
import Setting from '@shared/classes/setting';
import User from '@shared/classes/user';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import { pushRoute } from '@shared/store/thunks/route.thunk';
import { setFocus } from '@shared/store/slices/focus.slice';
import { setModal } from '@shared/store/slices/modal.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useMemo } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Displays a mosaic of settings options.
 */
export default function SettingsPage() {
  const settings = useSettingsPage();
  return (
    <MosaicTemplate
      animatable={false}
      canOpenFloatingMenu={false}
      complete={true}
      description={false}
      items={settings}
      startsInMenu={true}
    />
  );
}

/**
 * Hook to use the settings page.
 * Handles the settings page logic.
 */
function useSettingsPage() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { planId } = useAppSelector(userSelector);
  const { isAnonymous } = useAppSelector(userSelector);

  // Constants

  // Populate settings array

  // ------------------------------------------------------------
  // Keyboard hooks
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key: string) => {
      switch (key) {
        case 'RIGHT':
          dispatch(setFocus(Focus.MOSAIC));
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const plan = User.getPlan(planId);
    const canOrderChannels = PlanUtils.checkFeature(Features.ORDER_CHANNELS);
    const settings = [
      new Setting({
        title: 'PAGES.SETTINGS.ACCOUNT.CARD',
        image: Account,
        action: () => {
          dispatch(
            pushRoute({
              route: { Page: Pages.ACCOUNT },
              params: { stack: { index: 0 } },
            })
          );
        },
      }),
      new Setting({
        title: 'PAGES.SETTINGS.DISK.CARD',
        image: Disk,
        action: () => {
          if (isAnonymous) {
            dispatch(setModal(Modals.ANONYMOUS_GENERIC));
          } else {
            dispatch(
              pushRoute({
                route: { Page: Pages.DISK },
                params: { stack: { index: 1 } },
              })
            );
          }
        },
      }),
    ];

    if (isAnonymous || canOrderChannels) {
      const index = settings?.length || 0;
      settings.push(
        new Setting({
          title: 'PAGES.SETTINGS.MANAGE_CHANNELS.CARD',
          image: Channels,
          action: () => {
            if (isAnonymous) {
              dispatch(setModal(Modals.ANONYMOUS_GENERIC));
            } else {
              dispatch(
                pushRoute({
                  route: { Page: Pages.MANAGE_CHANNELS },
                  params: { stack: { index } },
                })
              );
            }
          },
        })
      );
    }

    if (FlavourConfig.languages.length > 1) {
      const index = settings?.length || 0;
      settings.push(
        new Setting({
          title: 'PAGES.SETTINGS.LANGUAGES.CARD',
          image: Language,
          action: () => {
            dispatch(
              pushRoute({
                route: { Page: Pages.SETTING_LANGUAGES },
                params: { stack: { index } },
              })
            );
          },
        })
      );
    }

    if (plan?.features.includes(Features.POWER_SAVING)) {
      const index = settings?.length || 0;
      settings.push(
        new Setting({
          title: 'PAGES.SETTINGS.POWER_SAVING.TITLE',
          image: PowerSaving,
          action: () => {
            dispatch(
              pushRoute({
                route: { Page: Pages.POWER_SAVING },
                params: { stack: { index } },
              })
            );
          },
        })
      );
    }

    if (!isAnonymous && settings.length) {
      const index = settings?.length || 0;
      settings.push(
        new Setting({
          title: 'PAGES.SETTINGS.LOGOUT.CARD',
          image: Logout,
          action: () => {
            dispatch(
              pushRoute({
                route: { Page: Pages.LOGOUT },
                params: { stack: { index } },
              })
            );
          },
        })
      );
    }

    return settings;
  }, []);
}
